// eslint-disable-next-line simple-import-sort/imports
import { scan } from 'react-scan';

import { Auth } from '@optools/app/providers/auth';
import { ConfigProvider } from '@optools/app/providers/config';
import { ProfileProvider } from '@optools/app/providers/profile';

import { routeTree } from '@optools/routeTree.gen';

import { setupStore } from '@optools/store/store';
import { ThemeProvider } from '@optools/components/theme-provider';
import { createRouter } from '@tanstack/react-router';
import { StrictMode } from 'react';
import * as ReactDOM from 'react-dom/client';
import { Provider } from 'react-redux';
import { App } from '@optools/app/app';
import { useAppSelector } from '@optools/store/hooks';
import { selectOperatorGatewayProxyConfig } from '@optools/store/configApiSlice';
import { Apollo } from '@optools/app/providers/apollo';
import { DataDog } from '@optools/app/providers/datadog/datadog';
import { TooltipProvider } from '@optools/components/ui/tooltip';
import { domAnimation, LazyMotion } from 'motion/react';
declare module '@tanstack/react-router' {
  interface Register {
    router: typeof router;
  }
}

const store = setupStore();

const router = createRouter({
  context: { auth: undefined!, config: undefined!, profile: undefined! },
  defaultStructuralSharing: true,
  InnerWrap: ({ children }) => {
    const mlbOperatorGatewayProxyConfig = useAppSelector(selectOperatorGatewayProxyConfig);
    return (
      <Apollo mlbOperatorGatewayProxyConfig={mlbOperatorGatewayProxyConfig}>
        <DataDog>
          <TooltipProvider>{children}</TooltipProvider>
        </DataDog>
      </Apollo>
    );
  },
  notFoundMode: 'root',
  routeTree,
  scrollRestoration: true,
  scrollToTopSelectors: ['#game-detail-navigation'],
});

function renderApp() {
  const rootElement = document.getElementById('root');
  if (rootElement && !rootElement.innerHTML) {
    const root = ReactDOM.createRoot(rootElement);
    root.render(
      <StrictMode>
        <LazyMotion features={domAnimation} strict>
          <ThemeProvider>
            <Provider store={store}>
              <ConfigProvider>
                <Auth
                  onRestoreOriginalUri={async (uri) => {
                    await router.invalidate({ sync: true });
                    router.history.replace(uri);
                  }}>
                  <ProfileProvider>
                    <App router={router} />
                  </ProfileProvider>
                </Auth>
              </ConfigProvider>
            </Provider>
          </ThemeProvider>
        </LazyMotion>
      </StrictMode>,
    );
  }
}

if (process.env.NODE_ENV === 'development' && REACT_SCAN === 'enabled') {
  scan({ trackUnnecessaryRenders: true });
}

async function enableApiMocking() {
  const { worker } = await import('@optools/test-utils/browser');
  await worker.start({ onUnhandledRequest: 'bypass' });
}

void (API_MOCKING === 'enabled'
  ? enableApiMocking()
      .then(() => renderApp())
      .catch(console.error)
  : renderApp());
